import React, { useState } from 'react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { X, Mail, Lock, Bot, ArrowRight } from 'lucide-react';

export default function AuthModal({ isOpen, onClose, onLoginSuccess }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      onLoginSuccess();
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      onLoginSuccess();
    } catch (err) {
      setError(err.message.replace('Firebase: ', ''));
    }
  };

  return (
    <div className="fixed inset-0 bg-[#050507]/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div className="bg-[#0a0a12] border border-[#02B6FF]/20 rounded-3xl p-8 max-w-md w-full shadow-2xl relative animate-in zoom-in-95 duration-200">
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-slate-500 hover:text-white bg-[#030306]/85 hover:bg-slate-700 p-2 rounded-full transition"
        >
          <X size={18} />
        </button>

        <div className="w-16 h-16 bg-gradient-to-br from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#02B6FF]/20 ring-4 ring-[#02B6FF]/10">
          <Bot size={32} className="text-white" />
        </div>

        <h2 className="text-3xl font-black text-white mb-2 tracking-tight">
          {isSignUp ? 'Activate Account' : 'Welcome Back'}
        </h2>
        <p className="text-slate-400 mb-8 font-medium">
          {isSignUp ? 'Join the neural engine partner waitlist.' : 'Access your affiliate dashboard.'}
        </p>

        {error && (
          <div className="bg-rose-500/10 border border-rose-500/50 text-rose-400 p-3 rounded-xl mb-6 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleEmailAuth} className="space-y-4 mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
              <Mail size={18} />
            </div>
            <input 
              type="email" 
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="operator@system.com" 
              className="w-full bg-[#030306]/80 border border-[#02B6FF]/10 text-white rounded-xl pl-11 pr-4 py-4 focus:outline-none focus:border-[#02B6FF] focus:ring-1 focus:ring-[#02B6FF]/20 transition shadow-inner font-mono text-sm"
            />
          </div>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
              <Lock size={18} />
            </div>
            <input 
              type="password" 
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" 
              className="w-full bg-[#030306]/80 border border-[#02B6FF]/10 text-white rounded-xl pl-11 pr-4 py-4 focus:outline-none focus:border-[#02B6FF] focus:ring-1 focus:ring-[#02B6FF]/20 transition shadow-inner font-mono text-sm"
            />
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] hover:from-[#33D5E6] hover:to-[#02B6FF] text-white rounded-xl font-black text-lg shadow-lg shadow-[#02B6FF]/20 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center group uppercase tracking-widest font-mono"
          >
            {loading ? 'Processing...' : (isSignUp ? 'Initialize Profile' : 'Access Engine')}
            {!loading && <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />}
          </button>
        </form>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-800"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase font-bold">
            <span className="bg-slate-900 px-4 text-slate-500">Or Continue With</span>
          </div>
        </div>

        <button 
          onClick={handleGoogleAuth}
          type="button" 
          className="w-full flex items-center justify-center space-x-3 py-4 bg-[#030306] border border-slate-700 hover:border-[#02B6FF]/40 text-slate-300 hover:text-white rounded-xl font-bold transition-all shadow-inner active:scale-95 mb-6"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <span>Continue with Google</span>
        </button>

        <p className="text-center text-sm text-slate-500 font-medium">
          {isSignUp ? "Already have access?" : "Need an invite code?"}{" "}
          <button 
            type="button" 
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
            }}
            className="text-[#33D5E6] hover:text-[#02B6FF] font-bold transition"
          >
            {isSignUp ? "Log In Here" : "Partner Access"}
          </button>
        </p>
      </div>
    </div>
  );
}
